import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import RealtimeApiClient from "../Helpers/realtimeApi/RealtimeApiClient";
import PcmAudioPlayer from "../Helpers/realtimeApi/PcmAudioPlayer";

const AUDIO_IDLE_MS = 500;
const FIRST_AUDIO_TIMEOUT_MS = 2500;
const MAX_AUDIO_MS = 10_000;
const MAX_TTS_RETRIES = 3;

const shouldTranslateText = (text) => {
  return typeof text === "string" && text.trim().length > 0;
};

export const useRealtimeTranslation = ({
  enabled,
  language,
  ttsEnabled,
  outputDeviceId,
} = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [translations, setTranslations] = useState(() => new Map());
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  useEffect(() => {}, []);

  const clientRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const isAudioPlayingRef = useRef(false);

  const desiredLanguageRef = useRef(language || null);
  const desiredTtsEnabledRef = useRef(Boolean(ttsEnabled));

  const queueRef = useRef([]);
  const pendingIdsRef = useRef(new Set());
  const retryAttemptsRef = useRef(new Map());
  const inFlightRef = useRef(null); // { messageId, text }

  const translationWaiterRef = useRef({ resolve: null, reject: null });

  const audioWaiterRef = useRef({
    resolve: null,
    timeout: null,
    idle: null,
    first: null,
    seenFirstFrame: false,
  });

  const clearAudioWaiter = useCallback(() => {
    const waiter = audioWaiterRef.current;
    if (waiter.idle) {
      clearTimeout(waiter.idle);
    }
    if (waiter.first) {
      clearTimeout(waiter.first);
    }
    if (waiter.timeout) {
      clearTimeout(waiter.timeout);
    }
    audioWaiterRef.current = {
      resolve: null,
      timeout: null,
      idle: null,
      first: null,
      seenFirstFrame: false,
    };
  }, []);

  const stopAudioPlayback = useCallback(() => {
    const waiter = audioWaiterRef.current;
    if (waiter.resolve) {
      waiter.seenFirstFrame = true;
      waiter.resolve();
    }
    clearAudioWaiter();
    audioPlayerRef.current?.stop();
    isAudioPlayingRef.current = false;
    setIsAudioPlaying(false);
  }, [clearAudioWaiter]);

  const clearQueue = useCallback(() => {
    queueRef.current = [];
    pendingIdsRef.current = new Set();
    retryAttemptsRef.current = new Map();
    inFlightRef.current = null;

    const waiter = translationWaiterRef.current;
    if (waiter.reject) {
      waiter.reject(new Error("translation_cancelled"));
    }
    translationWaiterRef.current = { resolve: null, reject: null };
  }, []);

  const ensureClient = useCallback(() => {
    if (clientRef.current) {
      return clientRef.current;
    }

    const client = new RealtimeApiClient({
      onOpen: () => {
        setConnectionError(null);
        setIsConnected(true);
      },
      onClose: () => {
        setIsConnected(false);

        // Requeue in-flight first.
        const inFlight = inFlightRef.current;
        if (inFlight) {
          queueRef.current.unshift(inFlight);
          retryAttemptsRef.current.set(inFlight.messageId, 0);
          inFlightRef.current = null;
        }

        // Reject any waiter so pump can resume after reconnect.
        const waiter = translationWaiterRef.current;
        if (waiter.reject) {
          waiter.reject(new Error("disconnected"));
        }
        translationWaiterRef.current = { resolve: null, reject: null };

        // Silent retry.
        client.scheduleReconnect();
      },
      onError: (err) => {
        setConnectionError(err);
      },
      onTranslatedText: (payload) => {
        const waiter = translationWaiterRef.current;
        if (waiter.resolve) {
          waiter.resolve(payload);
        }
        translationWaiterRef.current = { resolve: null, reject: null };
      },
      onAudio: (pcm) => {
        if (!desiredTtsEnabledRef.current) {
          return;
        }

        // Only play audio when we are currently waiting for the active message.
        // This avoids playing stale audio after a language change (we drop in-flight work).
        const waiter = audioWaiterRef.current;
        if (!waiter.resolve) {
          return;
        }

        // Best-effort init (may fail due to autoplay policies).
        if (audioPlayerRef.current && !audioPlayerRef.current.isInitialized) {
          audioPlayerRef.current.initialize().catch(() => {
            // ignore
          });
        }

        if (!isAudioPlayingRef.current) {
          isAudioPlayingRef.current = true;
          setIsAudioPlaying(true);
        }
        audioPlayerRef.current?.pushPcmChunk(pcm);

        if (!waiter.seenFirstFrame) {
          waiter.seenFirstFrame = true;
          if (waiter.first) {
            clearTimeout(waiter.first);
            waiter.first = null;
          }
        }

        if (waiter.idle) {
          clearTimeout(waiter.idle);
        }
        waiter.idle = setTimeout(() => {
          const latest = audioWaiterRef.current;
          if (latest.resolve) {
            latest.resolve();
          }
        }, AUDIO_IDLE_MS);
        audioWaiterRef.current = waiter;
      },
    });

    clientRef.current = client;
    return client;
  }, []);

  const ensureAudioPlayer = useCallback(() => {
    if (!audioPlayerRef.current) {
      audioPlayerRef.current = new PcmAudioPlayer({
        initialBufferMs: 200,
        minChunkMs: 60,
      });
    }

    if (outputDeviceId) {
      audioPlayerRef.current.setOutputDevice(outputDeviceId);
    }

    return audioPlayerRef.current;
  }, [outputDeviceId]);

  const waitForAudioCompletion = useCallback(async () => {
    if (!desiredTtsEnabledRef.current) {
      isAudioPlayingRef.current = false;
      setIsAudioPlaying(false);
      return true;
    }

    // Wait for first audio frame up to FIRST_AUDIO_TIMEOUT_MS.
    // Once the first frame arrives, we resolve after AUDIO_IDLE_MS of silence.
    const waiter = {
      resolve: null,
      idle: null,
      first: null,
      timeout: null,
      seenFirstFrame: false,
    };

    await new Promise((resolve) => {
      clearAudioWaiter();

      const first = setTimeout(() => {
        resolve();
      }, FIRST_AUDIO_TIMEOUT_MS);

      const timeout = setTimeout(() => {
        resolve();
      }, MAX_AUDIO_MS);

      waiter.resolve = resolve;
      waiter.first = first;
      waiter.timeout = timeout;
      audioWaiterRef.current = waiter;
    });

    clearAudioWaiter();

    await audioPlayerRef.current?.waitForDrain({ timeoutMs: MAX_AUDIO_MS });
    isAudioPlayingRef.current = false;
    setIsAudioPlaying(false);

    return waiter.seenFirstFrame;
  }, [clearAudioWaiter]);

  const pump = useCallback(async () => {
    if (!enabled || !clientRef.current?.isConnected) {
      return;
    }

    if (inFlightRef.current) {
      return;
    }

    const next = queueRef.current.shift();
    if (!next) {
      return;
    }

    inFlightRef.current = next;

    try {
      const client = ensureClient();

      const translationPayload = await new Promise((resolve, reject) => {
        translationWaiterRef.current = { resolve, reject };
        client.sendTranslateText(next.text);
      });

      const translatedText = translationPayload?.text;

      if (typeof translatedText === "string") {
        setTranslations((prev) => {
          const updated = new Map(prev);
          updated.set(next.messageId, translatedText);
          return updated;
        });
      }

      const sawAudio = await waitForAudioCompletion();
      if (desiredTtsEnabledRef.current && !sawAudio) {
        throw new Error("tts_audio_timeout");
      }

      // Mark completed.
      pendingIdsRef.current.delete(next.messageId);
      retryAttemptsRef.current.delete(next.messageId);
    } catch (error) {
      if (!clientRef.current?.isConnected) {
        return;
      }

      const attempts = retryAttemptsRef.current.get(next.messageId) || 0;
      const nextAttempts = attempts + 1;
      retryAttemptsRef.current.set(next.messageId, nextAttempts);

      if (nextAttempts <= MAX_TTS_RETRIES) {
        queueRef.current.unshift(next);
        console.warn("[RealtimeTranslation] Retrying TTS", {
          messageId: next.messageId,
          attempt: nextAttempts,
          error,
        });
      } else {
        console.warn("[RealtimeTranslation] TTS failed after retries", {
          messageId: next.messageId,
          error,
        });
        pendingIdsRef.current.delete(next.messageId);
        retryAttemptsRef.current.delete(next.messageId);
      }
    } finally {
      inFlightRef.current = null;
      pump();
    }
  }, [enabled, ensureClient, waitForAudioCompletion]);

  const translateMessage = useCallback(
    (originalMessage, messageId) => {
      if (!enabled) {
        return;
      }

      if (messageId == null) {
        return;
      }

      if (!shouldTranslateText(originalMessage)) {
        return;
      }

      if (translations.has(messageId)) {
        return;
      }

      const pendingIds = pendingIdsRef.current;
      if (pendingIds.has(messageId)) {
        return;
      }

      pendingIds.add(messageId);
      retryAttemptsRef.current.set(messageId, 0);
      queueRef.current.push({ messageId, text: originalMessage });

      pump();
    },
    [enabled, pump, translations]
  );

  const setTtsEnabled = useCallback(
    (nextEnabled) => {
      desiredTtsEnabledRef.current = Boolean(nextEnabled);

      if (!enabled) {
        return;
      }

      const client = ensureClient();
      client
        .updateConfiguration({
          language: desiredLanguageRef.current,
          ttsEnabled: desiredTtsEnabledRef.current,
        })
        .catch(() => {
          // noop
        });

      if (!desiredTtsEnabledRef.current) {
        stopAudioPlayback();
      } else {
        // Best-effort init.
        ensureAudioPlayer().initialize().catch(() => {
          // noop
        });
      }
    },
    [enabled, ensureAudioPlayer, ensureClient, stopAudioPlayback]
  );

  useEffect(() => {
    desiredLanguageRef.current = language || null;
  }, [language]);

  useEffect(() => {
    desiredTtsEnabledRef.current = Boolean(ttsEnabled);
  }, [ttsEnabled]);

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      setConnectionError(null);
      setTranslations(new Map());
      isAudioPlayingRef.current = false;
      setIsAudioPlaying(false);
      stopAudioPlayback();
      clearQueue();

      clientRef.current?.disconnect();
      clientRef.current = null;

      audioPlayerRef.current?.destroy();
      audioPlayerRef.current = null;

      return;
    }

    ensureAudioPlayer();

    const client = ensureClient();
    client
      .connect({
        language: language || desiredLanguageRef.current,
        ttsEnabled: Boolean(ttsEnabled),
      })
      .catch((err) => {
        setConnectionError(err);
        setIsConnected(false);
        client.scheduleReconnect();
      });

    return () => {
      client.disconnect();
    };
  }, [clearQueue, enabled, ensureAudioPlayer, ensureClient, language, stopAudioPlayback, ttsEnabled]);

  // Apply output device changes (best-effort).
  useEffect(() => {
    if (outputDeviceId) {
      audioPlayerRef.current?.setOutputDevice(outputDeviceId);
    }
  }, [outputDeviceId]);

  // Language changes (simplest MVP): drop queued/in-flight, update config if connected.
  const previousLanguageRef = useRef(language || null);
  useEffect(() => {
    const next = language || null;

    if (!enabled) {
      previousLanguageRef.current = next;
      return;
    }

    const prev = previousLanguageRef.current;
    if (!next || next === prev) {
      return;
    }

    previousLanguageRef.current = next;
    desiredLanguageRef.current = next;

    stopAudioPlayback();
    clearQueue();

    if (clientRef.current?.isConnected) {
      clientRef.current
        .updateConfiguration({
          language: next,
          ttsEnabled: desiredTtsEnabledRef.current,
        })
        .catch(() => {});
    }
  }, [clearQueue, enabled, language, stopAudioPlayback]);

  // RealtimeApiClient requires explicit language in update_configuration.
  // Ensure we update the server when `ttsEnabled` prop changes.
  const previousTtsEnabledRef = useRef(Boolean(ttsEnabled));
  useEffect(() => {
    const next = Boolean(ttsEnabled);

    if (!enabled) {
      previousTtsEnabledRef.current = next;
      return;
    }

    const prev = previousTtsEnabledRef.current;
    if (prev === next) {
      return;
    }

    previousTtsEnabledRef.current = next;
    desiredTtsEnabledRef.current = next;

    if (clientRef.current?.isConnected) {
      clientRef.current
        .updateConfiguration({
          language: desiredLanguageRef.current,
          ttsEnabled: next,
        })
        .catch(() => {
          // noop
        });
    }

    if (!next) {
      stopAudioPlayback();
    }
  }, [enabled, stopAudioPlayback, ttsEnabled]);

  // Pump whenever we (re)connect.
  useEffect(() => {
    if (enabled && isConnected) {
      pump();
    }
  }, [enabled, isConnected, pump]);

  return useMemo(() => {
    return {
      isConnected,
      connectionError,
      translateMessage,
      translations,
      setTtsEnabled,
      stopAudioPlayback,
      isAudioPlaying,
    };
  }, [connectionError, isConnected, isAudioPlaying, setTtsEnabled, stopAudioPlayback, translateMessage, translations]);
};
